import { activityProjectId } from "./activityProject";
import { calendarTaskStyle } from "../deals/projectColors";
import { useGetIdentity } from "ra-core";
import { Link } from "react-router";
import {
  Building2,
  UserRound,
  BriefcaseBusiness,
  CheckSquare,
  MessageSquare,
  Pencil,
  RefreshCw,
  Receipt,
  Trash2,
} from "lucide-react";
import type { Activity } from "../types";
import { useGetSalesName } from "../sales/useGetSalesName";
import { RelativeDate } from "../misc/RelativeDate";
import { expenseCategoryLabel, rubles } from "../finance/expenseModel";
import { actorLabel } from "./activityModel";
import { personalTask } from "../tasks/calendarModel";

const events = {
  "company.created": ["Создание компании", Building2],
  "contact.created": ["Создание контакта", UserRound],
  "deal.created": ["Создание проекта", BriefcaseBusiness],
  "contactNote.created": ["Заметка о контакте", MessageSquare],
  "dealNote.created": ["Заметка о проекте", MessageSquare],
  "task.created": ["Создание задачи", CheckSquare],
  "task.updated": ["Изменение задачи", Pencil],
  "task.status_changed": ["Изменение статуса", RefreshCw],
  "task.comment_added": ["Комментарий к задаче", MessageSquare],
  "task.comment_deleted": ["Удаление комментария", Trash2],
  "task.expense_added": ["Расход по задаче", Receipt],
} as const;
const statuses: Record<string, string> = {
  new: "Новая",
  in_progress: "В работе",
  completed: "Выполнена",
};
const fields: Record<string, string> = {
  type: "Тип",
  text: "Описание",
  due_date: "Срок",
  sales_id: "Исполнитель",
  deal_id: "Проект",
  contact_id: "Контакт",
  scheduled_start: "Начало",
  scheduled_end: "Окончание",
  installation_start: "Первый день монтажа",
  installation_end: "Последний день монтажа",
  team_task: "Для всей команды",
};

export function ActivityEntry({
  activity,
  color,
}: {
  activity: Activity;
  color?: string;
}) {
  const { identity } = useGetIdentity();
  const name = useGetSalesName(activity.sales_id ?? undefined);
  const author = actorLabel(activity.sales_id, name, identity);
  const [action, Icon] = events[activity.type as keyof typeof events] ?? [
    "Действие",
    CheckSquare,
  ];
  const event = "taskActivity" in activity ? activity.taskActivity : undefined;
  const payload = event?.payload ?? {};
  const personal =
    identity?.administrator &&
    activity.type.startsWith("task.") &&
    personalTask(
      {
        sales_id: activity.task_sales_id ?? event?.task_sales_id,
        created_by_sales_id:
          activity.task_created_by_sales_id ?? event?.task_created_by_sales_id,
      },
      identity.id,
    );
  const company = "company" in activity ? activity.company : undefined;
  const contact = "contact" in activity ? activity.contact : undefined;
  const deal = "deal" in activity ? activity.deal : undefined;
  const contactNote =
    "contactNote" in activity ? activity.contactNote : undefined;
  const dealNote = "dealNote" in activity ? activity.dealNote : undefined;
  const object =
    company?.name ??
    (contact
      ? [contact.first_name, contact.last_name].filter(Boolean).join(" ")
      : null) ??
    deal?.name ??
    payload.task_text ??
    payload.text;
  const href = company
    ? `/companies/${company.id}/show`
    : contact
      ? `/contacts/${contact.id}/show`
      : deal
        ? `/deals/${deal.id}/show`
        : contactNote
          ? `/contacts/${contactNote.contact_id}/show`
          : dealNote
            ? `/deals/${dealNote.deal_id}/show`
            : undefined;
  const note = contactNote?.text ?? dealNote?.text ?? payload.comment;
  return (
    <article
      className="rounded-xl border bg-card p-3 sm:p-4"
      style={calendarTaskStyle(
        {
          status:
            payload.new_status === "completed" ||
            payload.task_status === "completed"
              ? "completed"
              : "new",
        },
        color ??
          deal?.calendar_color ??
          (activity.type.startsWith("contact")
            ? "#659db4"
            : activity.type === "company.created"
              ? "#79a58b"
              : undefined),
      )}
      data-activity-id={activity.id}
      data-project-id={activityProjectId(activity)}
      data-author-id={activity.sales_id}
      data-task-type={(activity as Activity & { task_type?: string }).task_type}
    >
      <div className="flex gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border bg-background/60">
          <Icon className="size-4" />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <span
              className="text-sm font-semibold break-words"
              data-activity-author
            >
              {author}
            </span>
            <span
              className="text-xs text-muted-foreground"
              title={new Date(activity.date).toLocaleString("ru-RU")}
            >
              <RelativeDate date={activity.date} />
            </span>
          </div>
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            {action}
            {personal && (
              <span className="rounded border px-1.5 py-0.5 text-[10px] font-medium">
                Личная
              </span>
            )}
          </p>
          {object && (
            <p className="text-sm font-medium break-words">
              {href ? (
                <Link className="hover:underline" to={href}>
                  {object}
                </Link>
              ) : (
                object
              )}
            </p>
          )}
          {!object && href && (
            <Link className="text-sm font-medium hover:underline" to={href}>
              {contactNote ? "Открыть контакт" : "Открыть проект"}
            </Link>
          )}
          {activity.type === "task.status_changed" && (
            <p className="text-sm">
              {statuses[payload.old_status ?? ""] ?? payload.old_status ?? "—"}{" "}
              →{" "}
              {statuses[payload.new_status ?? ""] ?? payload.new_status ?? "—"}
            </p>
          )}
          {activity.type === "task.updated" &&
            Object.keys(payload.changes ?? {}).length > 0 && (
              <p className="text-xs text-muted-foreground">
                {Object.keys(payload.changes ?? {})
                  .map((key) => fields[key] ?? key)
                  .join(" · ")}
              </p>
            )}
          {activity.type === "task.expense_added" && (
            <p className="text-sm font-medium">
              {expenseCategoryLabel(payload.category ?? "other")} ·{" "}
              {rubles(Number(payload.amount ?? 0))}
            </p>
          )}
          {note && (
            <p
              className={`mt-2 rounded-lg bg-muted/60 p-3 text-sm whitespace-pre-wrap break-words ${activity.type === "task.comment_deleted" ? "line-through text-muted-foreground" : ""}`}
            >
              {note}
            </p>
          )}
        </div>
      </div>
    </article>
  );
}
